import axios from 'axios'
import React, { useState } from 'react'
import {
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Popover,
  Divider,
  Chip,
  Tooltip
} from '@material-ui/core'
import LinkRoundedIcon from '@material-ui/icons/LinkRounded'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import AddApplicableLaw from 'components/AddApplicableLaw'
import AreYouSureModal from 'components/AreYouSureModal'
import { useDispatch } from 'react-redux'
import { renderLongText } from 'helpers/dataHelpers'
import { removeLawById } from 'utils/laws/actions'
import { checkErrorStatus, setSnackbar } from 'utils/common/actions'
import history from 'browserHistory'
import RowLink from 'components/RowLink'
import Config from 'config'
import useStyles from './styles'


const LawListItem = (props) => {
  const {
    id,
    short_name,
    url,
    country,
    articles,
    name,
    name_en,
    countries,
    handleListItemChange,
    additionalClasses,
    isStatic,
    isMobile,
    isAdvice,
    isLargeDesktop
  } = props
  const { apiUrl } = Config.network
  const dispatch = useDispatch()
  const localClasses = useStyles()
  const [anchorEl, setAnchorEl] = useState(null)
  const [anchorPopoverEl, setAnchorPopoverEl] = useState(null)
  const [showCreateLawDialog, setShowCreateLawDialog] = useState(false)
  const [showAreYouSureModal, setShowAreYouSureModal] = useState(false)

  const handleDeleteConfirm = (event) => {
    event.stopPropagation()
    const url = `${apiUrl}laws/${id}/`
    axios.delete(url).then((body) => {
      // const { data } = body
      dispatch(setSnackbar('Law deleted!'))
      dispatch(removeLawById(id))
      setShowAreYouSureModal(false)
    }).catch(err => {
      if (err.response) {
        dispatch(checkErrorStatus(err.response.status))
        dispatch(setSnackbar('Error while deleting advice', 'error'))
      } else {
        dispatch(setSnackbar('Connection error', 'error'))
      }
      setShowAreYouSureModal(false)
    })
  }

  const handlePopoverOpen = (event) => {
    setAnchorPopoverEl(event.currentTarget)
  };

  const handlePopoverClose = () => {
    setAnchorPopoverEl(null)
  }

  const handleClick = (event) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClickUrl = (event) => {
    event.stopPropagation()
    const win = window.open(url, '_blank');
    win.focus()
  }

  const handleClose = (event) => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  const handleDelete = (event) => {
    handleClose(event)
    setShowAreYouSureModal(true)
  }

  const handleEdit = (event) => {
    event.stopPropagation()
    setAnchorEl(null)
    history.push(`/laws/${id}?edit=True`)
  }

  const handleEditModal = (event) => {
    event.stopPropagation()
    setAnchorEl(null)
    setShowCreateLawDialog(true)
  }

  const handleRemoveFromList = (event) => {
    event.stopPropagation()
    setAnchorEl(null)
    handleListItemChange('remove', {id})
  }

  const getMaxChars = () => {
    if (isMobile) {
      return 12
    }
    if (!isAdvice) {
      return null
    }
    if (isLargeDesktop) {
      return 64
    }
    return null
  }

  // Inside an advice the law row is not a link to the law page
  const Row = isAdvice ? 'div' : RowLink
  const rowProps = isAdvice ? {} : { to: `/laws/${id}` }

  return (
    <>
      <Row
        id={id}
        className={`${localClasses.law} ${additionalClasses ? additionalClasses : ''}`}
        {...rowProps}
      >
        <div className={`${localClasses.title} title`}>
          <Tooltip
            title={
              <React.Fragment>
                {short_name}
              </React.Fragment>
            }
            placement='top'
            classes={{
              popper: localClasses.tooltip
            }}
            arrow
          >
            <Typography variant='body2'>
              {renderLongText(short_name, getMaxChars())}
            </Typography>
          </Tooltip>
        </div>
        {
          !isMobile &&
            <div className={localClasses.articles}>
              <Typography variant='body2' color='textSecondary'>
                {renderLongText(articles)}
              </Typography>
            </div>
        }
        {
          !isMobile &&
            <div className={localClasses.country}>
              <Typography variant='body2' color='textSecondary'>
                {renderLongText(country)}
              </Typography>
            </div>
        }
        <div
          className={localClasses.source}
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
          aria-owns={!!anchorPopoverEl ? 'mouse-over-popover' : undefined}
          aria-haspopup="true"
        >
          <Chip
            className={localClasses.chip}
            label='Open source'
            icon={<LinkRoundedIcon />}
            onClick={handleClickUrl}
          />
          <Popover
            id="mouse-over-popover"
            className={localClasses.popover}
            classes={{
              paper: localClasses.paperPopover,
            }}
            open={!!anchorPopoverEl}
            anchorEl={anchorPopoverEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus
          >
            <Typography>{url}</Typography>
          </Popover>
        </div>
        {
          !isStatic ?
            <>
              <div
                className={localClasses.actions}
              >
                <IconButton
                  aria-controls={`law-${id}-menu`}
                  aria-haspopup='true'
                  onClick={handleClick}
                  className={localClasses.formControl}
                >
                  <MoreHorizIcon />
                </IconButton>
                <Menu
                  id={`law-${id}-menu`}
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                >
                  <MenuItem onClick={isAdvice ? handleEditModal : handleEdit}>Edit</MenuItem>
                  {
                    handleListItemChange ?
                    <MenuItem onClick={handleRemoveFromList}>Remove from list</MenuItem> :
                    null
                  }
                  <MenuItem onClick={handleDelete}>Delete</MenuItem>
                </Menu>
              </div>
              {
                showCreateLawDialog &&
                  <AddApplicableLaw
                    setShowDialog={setShowCreateLawDialog}
                    showDialog={showCreateLawDialog}
                    applicableLawId={id}
                    getValue={handleListItemChange}
                    countries={countries}
                    lawData={{
                      short_name,
                      url,
                      country,
                      articles,
                      name,
                      name_en
                    }}
                  />
              }
            </> : null
        }
        
      </Row>
      {
        showAreYouSureModal &&
          <AreYouSureModal
            showDialog={showAreYouSureModal}
            setShowDialog={setShowAreYouSureModal}
            actionText='Delete'
            actionDescription='Are you sure you want to Delete this Law?'
            action={handleDeleteConfirm}
          />
      }
      <Divider />
    </>
  )
}

export default LawListItem
